import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Receipt, Calendar, Clock, ChevronDown, LogOut, Menu, X, Settings, BarChart3, Download, Lock, BookOpen } from 'lucide-react';
import { useAuth, userIsAdmin, userIsSupervisor, userHasRole, userIsReviewer, userIsEngineer, userIsManager, userIsAccountant } from '../../context/AuthContext';
import { VA_BLACK, AMBER, AMBER_DARK, BORDER_COLOR, BASE_CSS } from '../../styles/tokens';

const SIDEBAR_W = 230;

/**
 * MODULE_CONFIG — ordered: Dashboard, Gastos, Horas, Vacaciones, Contabilidad, Configuración
 * Vacaciones is disabled (blocked for later development).
 */
const MODULE_CONFIG = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    basePaths: ['/admin', '/dashboard'],
    check: () => true,
    exact: true,
  },
  {
    key: 'gastos',
    label: 'Gastos',
    icon: Receipt,
    basePaths: ['/employee', '/supervisor', '/admin/recibos', '/admin/receipt'],
    check: () => true,
  },
  {
    key: 'horas',
    label: 'Horas',
    icon: Clock,
    basePaths: ['/horas'],
    check: (user) => userIsSupervisor(user) || userIsEngineer(user) || userIsManager(user) || userIsReviewer(user) || userIsAdmin(user),
  },
  {
    key: 'vacaciones',
    label: 'Vacaciones',
    icon: Calendar,
    basePaths: ['/vacaciones'],
    check: () => true,
    disabled: true, // blocked for later development
  },
  {
    key: 'contabilidad',
    label: 'Sage Report',
    icon: Download,
    basePaths: ['/contabilidad'],
    check: (user) => userIsAccountant(user) || userIsReviewer(user),
  },
  {
    key: 'guias',
    label: 'Guías',
    icon: BookOpen,
    basePaths: ['/guias'],
    check: () => true,
  },
  {
    key: 'configuracion',
    label: 'Configuración',
    icon: Settings,
    basePaths: ['/admin/tablas', '/admin/configuracion', '/admin/auditoria'],
    check: (user) => userIsAdmin(user),
  },
];


/** Sub-links shown under each module (always visible, no collapse) */
function getModuleLinks(modKey, user) {
  if (modKey === 'gastos') {
    const links = [];
    if (userIsAdmin(user)) {
      links.push({ label: 'Todos los Recibos', path: '/admin/recibos' });
    } else if (userIsReviewer(user)) {
      links.push({ label: 'Mis Recibos', path: '/employee' });
      links.push({ label: 'Revisión Final', path: '/supervisor' });
    } else if (userIsEngineer(user) || userIsManager(user)) {
      links.push({ label: 'Mis Recibos', path: '/employee' });
      links.push({ label: 'Recibos por Aprobar', path: '/supervisor' });
    } else {
      // Employee (chofer): only Mis Recibos
      links.push({ label: 'Mis Recibos', path: '/employee' });
    }
    return links;
  }
  if (modKey === 'horas') {
    if (userIsAdmin(user)) {
      return [{ label: 'Toda Nómina', path: '/horas' }];
    }
    if (userIsReviewer(user)) {
      return [{ label: 'Revisión Final Nómina', path: '/horas' }];
    }
    if (userIsEngineer(user) || userIsManager(user)) {
      return [{ label: 'Nómina por Aprobar', path: '/horas' }];
    }
    // Supervisor: submit timesheets
    return [{ label: 'Nómina', path: '/horas' }];
  }
  if (modKey === 'contabilidad') {
    return [];
  }
  if (modKey === 'vacaciones') {
    return [];
  }
  if (modKey === 'dashboard') {
    return [];
  }
  if (modKey === 'guias') {
    return [];
  }
  if (modKey === 'configuracion') {
    return [
      { label: 'Tablas', path: '/admin/tablas' },
      { label: 'Ajustes', path: '/admin/configuracion' },
      { label: 'Auditoría', path: '/admin/auditoria' },
    ];
  }
  return [];
}

export default function PortalLayout({ children, activeModule = 'gastos' }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedModules, setCollapsedModules] = useState({});

  const roleLabel = userIsAdmin(user)     ? 'Administrador'
    : userIsReviewer(user)   ? 'Revisión Final'
    : userIsEngineer(user)   ? 'Ingeniero(a)'
    : userIsManager(user)    ? 'Gerente'
    : userIsAccountant(user) ? 'Contabilidad'
    : userIsSupervisor(user) ? 'Supervisor(a)'
    : 'Chofer';

  function isModuleActive(mod) {
    if (mod.exact) {
      return mod.basePaths.some(p => location.pathname === p || location.pathname === p + '/');
    }
    return mod.basePaths.some(p => location.pathname.startsWith(p));
  }

  function handleModuleClick(modKey) {
    const mod = MODULE_CONFIG.find(m => m.key === modKey);
    if (!mod || mod.disabled) return;
    const links = getModuleLinks(modKey, user);
    if (!links.length) {
      if (modKey === 'dashboard' && !userIsAdmin(user)) {
        navigate('/dashboard');
      } else {
        navigate(mod.basePaths[0]);
      }
      setMobileOpen(false);
      return;
    }
    // Toggle collapse for sections with sub-links
    const isCurrentlyCollapsed = collapsedModules[modKey];
    if (isCurrentlyCollapsed) {
      // Expanding — also navigate to first sub-link
      setCollapsedModules(prev => ({ ...prev, [modKey]: false }));
      navigate(links[0].path);
      setMobileOpen(false);
    } else {
      // Collapse it
      setCollapsedModules(prev => ({ ...prev, [modKey]: true }));
    }
  }

  function handleLinkClick(path) {
    navigate(path);
    setMobileOpen(false);
  }

  function isLinkActive(link, modKey) {
    if (modKey === 'vacaciones' && link.path === '/vacaciones') {
      return location.pathname === '/vacaciones' || location.pathname === '/vacaciones/';
    }
    if (modKey === 'horas' && link.path === '/horas') {
      return location.pathname === '/horas' || location.pathname === '/horas/';
    }
    if (modKey === 'configuracion' && link.path === '/admin/tablas') {
      return location.pathname.startsWith('/admin/tablas');
    }
    if (modKey === 'configuracion' && link.path === '/admin/configuracion') {
      return location.pathname.startsWith('/admin/configuracion');
    }
    if (modKey === 'configuracion' && link.path === '/admin/auditoria') {
      return location.pathname.startsWith('/admin/auditoria');
    }
    return location.pathname.startsWith(link.path);
  }

  const sidebarContent = (
    <>
      {/* Logo */}
      <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <img src="/newlogo.jpeg" alt="V&A" style={{ height: 26, width: 'auto' }} />
      </div>

      {/* Modules */}
      <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto' }}>
        {MODULE_CONFIG.filter(mod => mod.check(user)).map(mod => {
          const Icon = mod.icon;
          const active = mod.key === activeModule || isModuleActive(mod);
          const links = mod.disabled ? [] : getModuleLinks(mod.key, user);
          const isDisabled = !!mod.disabled;

          return (
            <div key={mod.key}>
              {/* Module header */}
              <button
                onClick={() => isDisabled ? null : handleModuleClick(mod.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '10px 18px',
                  fontSize: 13, fontWeight: active && !isDisabled ? '700' : '500',
                  color: isDisabled ? 'rgba(255,255,255,0.25)' : active ? '#fbbf24' : 'rgba(255,255,255,0.6)',
                  background: active && !isDisabled ? 'rgba(217,119,6,0.1)' : 'transparent',
                  border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'background 0.15s, color 0.15s',
                  textAlign: 'left',
                }}
              >
                <Icon size={16} />
                <span style={{ flex: 1 }}>{mod.label}</span>
                {isDisabled && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)',
                    display: 'flex', alignItems: 'center', gap: 3,
                  }}>
                    <Lock size={9} /> Próximamente
                  </span>
                )}
                {links.length > 0 && (
                  <ChevronDown size={14} style={{ opacity: 0.5, transform: collapsedModules[mod.key] ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.15s' }} />
                )}
              </button>

              {/* Sub-links — collapsible */}
              {links.length > 0 && !collapsedModules[mod.key] && (
                <div style={{ paddingBottom: 4 }}>
                  {links.map(link => {
                    const linkActive = isLinkActive(link, mod.key);
                    return (
                      <button
                        key={link.path + link.label}
                        onClick={() => handleLinkClick(link.path)}
                        style={{
                          display: 'block', width: '100%',
                          padding: '7px 18px 7px 44px',
                          fontSize: 12, fontWeight: linkActive ? 600 : 400,
                          color: linkActive ? '#fbbf24' : 'rgba(255,255,255,0.45)',
                          background: linkActive ? 'rgba(217,119,6,0.08)' : 'transparent',
                          border: 'none', cursor: 'pointer',
                          fontFamily: 'Inter, sans-serif',
                          textAlign: 'left',
                          borderLeft: linkActive ? '2px solid #fbbf24' : '2px solid transparent',
                          transition: 'all 0.12s',
                        }}
                      >
                        {link.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div style={{
        padding: '14px 18px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 500, marginBottom: 2 }}>
          {user?.name}
        </div>
        <div style={{
          display: 'inline-block',
          background: userIsAdmin(user) ? 'rgba(217,119,6,0.2)' : 'rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 600,
          color: userIsAdmin(user) ? '#fbbf24' : 'rgba(255,255,255,0.5)',
          marginBottom: 10,
        }}>
          {roleLabel}
        </div>
        <button
          onClick={logout}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            width: '100%', padding: '6px 0',
            background: 'transparent', border: 'none',
            color: 'rgba(255,255,255,0.4)', fontSize: 12,
            cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          }}
        >
          <LogOut size={14} />
          Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, ui-sans-serif, sans-serif' }}>
      <style>{BASE_CSS}{`
        .sidebar-desktop { display: flex; }
        .sidebar-mobile-toggle { display: none; }
        .sidebar-mobile-overlay { display: none; }
        @media (max-width: 768px) {
          .sidebar-desktop { display: none !important; }
          .sidebar-mobile-toggle { display: flex !important; }
          .sidebar-mobile-overlay { display: block !important; }
          .main-content { margin-left: 0 !important; }
        }
      `}</style>

      {/* Desktop sidebar */}
      <div className="sidebar-desktop" style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: SIDEBAR_W, background: VA_BLACK,
        display: 'flex', flexDirection: 'column',
        zIndex: 200,
      }}>
        {sidebarContent}
      </div>

      {/* Mobile top bar + hamburger */}
      <div className="sidebar-mobile-toggle" style={{
        display: 'none', alignItems: 'center', justifyContent: 'space-between',
        background: VA_BLACK, color: '#fff', height: 50,
        padding: '0 16px', position: 'sticky', top: 0, zIndex: 150,
      }}>
        <img src="/newlogo.jpeg" alt="V&A" style={{ height: 22 }} />
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="sidebar-mobile-overlay">
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              zIndex: 190,
            }}
          />
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: SIDEBAR_W, background: VA_BLACK,
            display: 'flex', flexDirection: 'column',
            zIndex: 200,
          }}>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="main-content" style={{ marginLeft: SIDEBAR_W }}>
        {children}
      </div>
    </div>
  );
}
